import React, { useRef, useEffect } from 'react';
import { Canvas, PencilBrush } from 'fabric';
import { useStoreValue, storeSet } from '../../store';
import '../../assets/components/PenCanvas.css';

/**
 * Horizontal extent of every stroke, in canvas pixels as they are on screen now
 * (scene coordinates run through the zoom-scaled viewport transform).
 */
const strokesExtentPx = (fc) => {
    const objects = fc.getObjects();
    if (!objects.length) return null;
    const vt = fc.viewportTransform;
    let left = Infinity;
    let right = -Infinity;
    objects.forEach(obj => {
        const rect = obj.getBoundingRect();
        left = Math.min(left, rect.left * vt[0] + vt[4]);
        right = Math.max(right, (rect.left + rect.width) * vt[0] + vt[4]);
    });
    return { left, right };
};

/**
 * @param {function} [pxToSec] canvas x (px) → timeline seconds; when given, the
 *   snapshot carries the `range` in seconds the strokes cover.
 */
export default function PenCanvas({ isActive, viewData = null, pxToSec = null }) {
    const wrapperRef = useRef(null);
    const pxToSecRef = useRef(pxToSec);
    pxToSecRef.current = pxToSec;
    const canvasElRef = useRef(null);
    const fabricRef = useRef(null);
    const prevDimsRef = useRef({ width: 0, height: 0 });
    const penCanvasResetSignal = useStoreValue('penCanvasResetSignal');

    useEffect(() => {
        const wrapper = wrapperRef.current;
        const canvasEl = canvasElRef.current;
        if (!wrapper || !canvasEl) return;

        const { width, height } = wrapper.getBoundingClientRect();

        const fc = new Canvas(canvasEl, {
            isDrawingMode: false,
            width: width || 300,
            height: height || 100,
        });

        const brush = new PencilBrush(fc);
        brush.color = '#ff827c';
        brush.width = 1.5;
        fc.freeDrawingBrush = brush;

        fc.on('path:created', () => {
            const extent = strokesExtentPx(fc);
            const toSec = pxToSecRef.current;
            const range = extent && toSec
                ? { start: toSec(extent.left), end: toSec(extent.right) }
                : null;
            storeSet('penCanvasData', { json: fc.toJSON(), svg: fc.toSVG(), range });
        });

        fabricRef.current = fc;
        prevDimsRef.current = { width: width || 300, height: height || 100 };

        const observer = new ResizeObserver(entries => {
            const { width: w, height: h } = entries[0].contentRect;
            const canvas = fabricRef.current;
            if (!canvas || w <= 0 || h <= 0) return;

            const { width: prevW, height: prevH } = prevDimsRef.current;

            if (prevW > 0 && prevH > 0) {
                // Scale the viewport transform so existing strokes stretch/squeeze
                // proportionally with the timeline as zoom or track length changes
                const sx = w / prevW;
                const sy = h / prevH;
                const vt = [...canvas.viewportTransform];
                vt[0] *= sx; // scaleX
                vt[3] *= sy; // scaleY
                vt[4] *= sx; // translateX
                vt[5] *= sy; // translateY
                canvas.viewportTransform = vt;
            }

            canvas.setDimensions({ width: w, height: h });
            canvas.requestRenderAll();
            prevDimsRef.current = { width: w, height: h };
        });
        observer.observe(wrapper);

        return () => {
            observer.disconnect();
            fc.dispose();
            fabricRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (!fabricRef.current) return;
        fabricRef.current.isDrawingMode = isActive;
    }, [isActive]);

    useEffect(() => {
        if (!fabricRef.current || penCanvasResetSignal === 0) return;
        fabricRef.current.clear();
        storeSet('penCanvasData', null);
    }, [penCanvasResetSignal]);

    useEffect(() => {
        const fc = fabricRef.current;
        if (!fc) return;
        if (viewData) {
            fc.loadFromJSON(viewData).then(() => {
                fc.isDrawingMode = false;
                fc.requestRenderAll();
            });
        } else if (!isActive) {
            fc.clear();
        }
    }, [viewData]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div
            ref={wrapperRef}
            className={`pen-canvas${isActive ? ' pen-canvas--active' : ''}${viewData ? ' pen-canvas--view' : ''}`}
        >
            <canvas ref={canvasElRef} />
        </div>
    );
}
